import { format } from 'date-fns'
import { Grid } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { useAppDispatch } from '../../../../../redux/store'
import Button from '../../../../../shared/UI/Button'
import { AbsenceOccurrence } from '../../../../../types/absence'
import RTWNetherlandsPrint from '../../../NetherlandsRTWPrint'
import { openPrintWindow } from '../../../../../utils/printHelper'
import { useRtwOperations } from '../../../../../hooks/useRtwOperations'
import { useRtwFormState } from '../../../../../hooks/useRtwFormState'
import { showErrorMessage, showSuccessMessage } from '../../../../../redux/reducers/snackbarReducer'
import UserErrorMessage from '../../../../../utils/errorFilter'
import { canPrintRtw } from '../../../../../utils/rtwUtils'

// Components
import AbsenceInfoSection from './components/AbsenceInfoSection'
import ReturnDateSection from './components/ReturnDateSection'
import ProceduresSection from './components/ProceduresSection'
import RecurrentSection from './components/RecurrentSection'
import CompanyActionSection from './components/CompanyActionSection'
import CommentsSection from './components/CommentsSection'

type Props = {
  absenceData: AbsenceOccurrence
  onClose: () => void
  onCancel: () => void
  onPrint: () => void
}

function NetherlandsRTW({ absenceData, onClose, onCancel, onPrint }: Props) {
  const dispatch = useAppDispatch()
  const [submissible, setSubmissible] = useState<boolean>(true)
  const [signedByHtml, setSignedByHtml] = useState<string>('')
  const didMountRef = useRef(false)

  // Use custom hooks
  const { rtwData, savedRtwData, updateField, updateSavedData } = useRtwFormState(absenceData)
  const { fetchRtwData: fetchRtwFromService, saveRtwData, insertRtwData } = useRtwOperations()

  // Update submissible state when form data changes
  useEffect(() => {
    if (didMountRef.current) {
      setSubmissible(true)
    }
    didMountRef.current = true
  }, [rtwData])

  // Fetch existing RTW data if available
  const fetchRtwData = async (absenceId: number) => {
    try {
      const result = await fetchRtwFromService(absenceId)

      const updatedData = {
        returnDate: format(new Date(result.returnDate!), 'yyyy-MM-dd'),
        proceduresComments: result.proceduresComments!,
        proceduresFollowed: result.proceduresFollowed!,
        recurrentComments: result.recurrentComments!,
        recurrent: result.recurrent!,
        companyActionComments: result.companyActionComments!,
        companyAction: result.companyAction!,
        additionalComments: result.additionalComments!,
      }

      updateSavedData(updatedData)
      setSignedByHtml(result.signedBy!)

      return result
    } catch (err: any) {
      dispatch(showErrorMessage(UserErrorMessage(err)))
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    setSubmissible(false)
    try {
      if (absenceData.hasValidRtw) {
        await saveRtwData(rtwData)
        dispatch(showSuccessMessage('RTW updated successfully'))
      } else {
        await insertRtwData(rtwData)
        dispatch(showSuccessMessage('RTW submitted successfully'))
      }
      onClose()
    } catch (err: any) {
      setSubmissible(true)
      dispatch(showErrorMessage(UserErrorMessage(err)))
    }
  }

  // Handle print functionality
  const handlePrint = () => {
    const content = renderToString(
      <RTWNetherlandsPrint signedByHtml={signedByHtml} absenceData={absenceData} rtwData={savedRtwData} />
    )
    openPrintWindow(content, 'Return To Work')
    onPrint()
  }

  // Fetch RTW data on component mount if available
  useEffect(() => {
    if (absenceData.hasValidRtw) {
      fetchRtwData(absenceData.id)
    }
  }, [absenceData.hasValidRtw, absenceData.id])

  const isPrintDisabled = !canPrintRtw(absenceData.absenceStatus.name || '', absenceData.hasValidRtw)

  return (
    <Grid container xl={12} rowSpacing={4} pr={4} pb={4}>
      {/* Left Column - Absence Information */}
      <AbsenceInfoSection absenceData={absenceData} />

      {/* Right Column - Form Sections */}
      <Grid item xs={12} lg={6}>
        <Grid container xl={12} alignContent="flex-start">
          <ReturnDateSection 
            returnDate={rtwData.returnDate} 
            onDateChange={updateField} 
          />
          
          <ProceduresSection
            proceduresFollowed={rtwData.proceduresFollowed}
            proceduresComments={rtwData.proceduresComments}
            onFieldChange={updateField}
          />

          <RecurrentSection
            recurrent={rtwData.recurrent}
            recurrentComments={rtwData.recurrentComments}
            onFieldChange={updateField}
          />

          <CompanyActionSection
            companyAction={rtwData.companyAction}
            companyActionComments={rtwData.companyActionComments}
            onFieldChange={updateField}
          />

          <CommentsSection
            additionalComments={rtwData.additionalComments}
            onFieldChange={updateField}
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Grid item sm={6} display="flex" justifyContent="flex-start">
        <Button
          label="Print"
          color="secondary"
          variant="outlined"
          onClick={handlePrint}
          disabled={isPrintDisabled}
        />
      </Grid>
      <Grid item sm={6} display="flex" gap={4} justifyContent="flex-end">
        <Button label="Cancel" color="secondary" variant="outlined" onClick={onCancel} />
        <Button
          label={absenceData.hasValidRtw ? 'Re-Submit' : 'Submit'}
          onClick={handleSubmit}
          loading={!submissible}
        />
      </Grid>
    </Grid>
  )
}

export default NetherlandsRTW